import { useMemo, useState } from 'react';
import { Flex, StackScreen, useSafeArea } from '@banhala/ui-kit';
import { WebView } from 'shared/src/component/WebWebView';
import { isNotNil, withWebviewOrigin } from 'shared/src/utils';
import { useScreensRoute } from '../../hooks/useScreensRoute';
import { useWebViewActionFromMessage } from 'shared/src/hooks';
import { resolveWebViewHeader } from './headerVisibleRoutes';
import { CartIcon, SearchIcon } from 'shared/src/component';
import { useNavigationGoBack } from 'shared/src/hooks';
import { useNavigation } from '@react-navigation/native';
import { EMBEDDED_WEB_QUERY_KEY } from 'shared/src/constants/webviewQuery';

/**
 * 범용 WebView 화면.
 *
 * `navigation.navigate('WebViewScreen', { url, title })`로 호출.
 * - 앱: react-navigation stack에 push
 * - 웹: history.pushState로 push → `location.href` 방식 대비 기존 스크린 상태 유지
 *
 * 전달받은 url에는 항상 다음 쿼리 파라미터를 주입한다:
 * - `global_layout=app` — 웹페이지가 앱 레이아웃(header/footer 제거)으로 렌더
 * - `title_bar=false`  — 웹쪽 타이틀바 숨김
 *
 * 네이티브 헤더는 기본적으로 숨기고(=웹뷰 전체 화면), `headerVisibleRoutes` 의
 * 화이트리스트에 해당하는 경로에서만 노출한다. 헤더를 노출하는 경우 caller 가
 * title 을 넘기지 않았으면 HTML 의 `<title>` 을 fetch 해서 사용한다.
 */
export const WebViewScreen = () => {
  const navigation = useNavigation();
  const { params } = useScreensRoute<'WebViewScreen'>();
  const { url, title } = params;
  const { top, bottom } = useSafeArea();
  const onPressBack = useNavigationGoBack();

  const finalUrl = useMemo(() => withWebViewParams(url), [url]);
  // path → 헤더 컴포넌트 매칭. 매칭이 없으면 헤더 없음(웹뷰 전체 화면).
  const header = useMemo(() => resolveWebViewHeader(url), [url]);
  const isDefaultHeader = header?.type === 'default';
  const CustomHeader = header?.type === 'custom' ? header.Component : null;
  const showHeader = header != null;

  const [webTitle, setWebTitle] = useState<string | undefined>(undefined);
  const pageTitle = title ?? webTitle ?? '';

  const webViewAction = useWebViewActionFromMessage({
    onChangeTitle: title => {
      setWebTitle(title);
      navigation.setOptions({ title });
    },
  });
  return (
    <StackScreen
      title={pageTitle}
      hideHeader={!isDefaultHeader}
      onPressBack={onPressBack}
      rightIcons={isDefaultHeader ? [<SearchIcon />, <CartIcon />] : undefined}
    >
      {CustomHeader != null && <CustomHeader />}
      <Flex flex={1} padding={{ top: showHeader ? 0 : top, bottom }}>
        {isNotNil(finalUrl) && (
          <WebView source={{ uri: finalUrl }} onMessage={webViewAction} />
        )}
      </Flex>
    </StackScreen>
  );
};

/**
 * url에 in-app WebView용 공통 쿼리 파라미터를 주입.
 * 이미 있으면 덮어쓰고, 파싱 실패 시 원본 반환.
 */
const withWebViewParams = (url: string | undefined): string | undefined => {
  if (!isNotNil(url)) return undefined;
  try {
    const parsed = new URL(url);
    parsed.searchParams.set('global_layout', 'app');
    parsed.searchParams.set('title_bar', 'false');
    // iframe 임베드임을 콘텐츠에 명시한다(프레임 구조 추측 대신 호스트 선언).
    parsed.searchParams.set(EMBEDDED_WEB_QUERY_KEY, 'true');
    return withWebviewOrigin(parsed);
  } catch {
    return url;
  }
};
